import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickAwayListener, InputBase, Typography } from '@material-ui/core';
import { FieldError, Option } from '@spike/model';
import { useNonInitialEffect } from '@versiondos/hooks';
import clsx from 'clsx';
import { InvoiceDiscount as InvoiceDiscountModel } from '@spike/invoice-model';
import React, { ChangeEvent } from 'react';
import { useCommonStyles } from 'components/UI/CommonStyles';
import { useSingleSaleTableItemDiscountStyles } from 'components/Payments/SingleSale/Table/Item/Discount/SingleSaleTableItemDiscount.styles';
import { Discount } from '@spike/order-model';

interface DiscountProps {
    name?: string;
    className?: string;
    discount?: Discount;
    errors?: Array<FieldError>;
    onChange?: (discount: InvoiceDiscountModel, name?: string) => void;
}

const usd: Option<string> = { id: 'usd', name: 'USD', description: '$' };
const percentage: Option<string> = { id: '%', name: '%', description: '%' };

const options: Array<Option<string>> = [usd, percentage];

export const SingleSaleTableItemDiscount: React.FC<DiscountProps> = props => {
    const commonClasses = useCommonStyles();
    const classes = useSingleSaleTableItemDiscountStyles();

    const [selected, setSelected] = React.useState<Option<string>>(props.discount?.percentage ? percentage : usd);
    const [showList, setShowList] = React.useState(false);
    const [value, setValue] = React.useState(
        props.discount?.percentage?.toString() || props.discount?.amount?.toString() || '0.00'
    );

    const hasError = props.errors?.some(error => error.fieldName === props.name);

    useNonInitialEffect(() => {
        const timer: NodeJS.Timer = setTimeout(() => {
            props.onChange &&
                props.onChange(
                    selected.id === usd.id
                        ? {
                              amount: value
                          }
                        : {
                              percentage: Number(value)
                          },
                    props.name
                );
        }, 200);
        return () => clearTimeout(timer);
    }, [value]);

    const selectHandler = (option: Option<string>) => {
        setSelected(option);
        setValue('0');
        setShowList(false);
    };

    const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const text = event.target.value;
        const number = Number(text);

        if (text === '' || (!isNaN(number) && number >= 0)) {
            setValue(text);
        }
    };

    const dollarAdornment =
        selected.id === usd.id ? (
            <Typography className={clsx(commonClasses.typo16_400, classes.adornment)}>$</Typography>
        ) : null;

    return (
        <div
            className={clsx(classes.container, props.className, {
                [commonClasses.errorBorder]: hasError,
                [commonClasses.errorText]: hasError
            })}
        >
            <div className={classes.valueContainer}>
                <InputBase
                    className={clsx(commonClasses.typo16_400, classes.inputRight, {
                        [commonClasses.errorText]: hasError
                    })}
                    value={value}
                    placeholder="0.00"
                    onChange={changeHandler}
                    startAdornment={dollarAdornment}
                />
            </div>

            <div
                className={classes.dropdownContainer}
                onClick={() => setShowList(true)}
            >
                <Typography
                    className={clsx(classes.text, {
                        [commonClasses.errorText]: hasError
                    })}
                >
                    {selected.name}
                </Typography>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    className={classes.chevron}
                />
            </div>

            {showList && (
                <ClickAwayListener onClickAway={() => setShowList(false)}>
                    <div className={classes.list}>
                        {options.map(option => (
                            <Typography
                                key={option.id}
                                className={classes.listItem}
                                onClick={() => selectHandler(option)}
                            >
                                {option.name}
                            </Typography>
                        ))}
                    </div>
                </ClickAwayListener>
            )}
        </div>
    );
};
